import { useMutation, useQuery } from '@tanstack/react-query'
import { ArrowRightIcon, LinkIcon } from 'lucide-react'
import { useId, useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router'

import { errorMessage } from '@/api/client'
import { invitationPreview, verify } from '@/api/endpoints'
import type { VerifyRequest, VerifyResponse } from '@/api/types'
import { CopyableValue } from '@/components/CodeBlock'
import { AsideNotes, PublicLayout } from '@/components/PublicLayout'
import { SecretTokenPanel } from '@/components/SecretTokenPanel'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/auth/useAuth'

import { landingAfterVerify, nameFieldValue, revealsToken } from './verify-view'

/** Client-side floor only — Keycloak's realm policy is the real authority. */
const MIN_PASSWORD_LENGTH = 12

const ASIDE = [
  {
    term: 'Your name',
    detail: 'Shown in the console and on your account. It is not your session name — sessions pick those themselves with connect().',
  },
  {
    term: 'Your password',
    detail: 'Used to sign in to the web console through Keycloak. It is never used by the MCP endpoint, which authenticates with a bearer token instead.',
  },
  {
    term: 'One shot at the token',
    detail: 'The raw bearer token appears on the next screen and nowhere else, ever. saltseed keeps only its SHA-256 hash.',
  },
] as const

function MissingToken() {
  return (
    <PublicLayout>
      <div className="space-y-7">
        <div className="flex size-11 items-center justify-center rounded-lg border border-border bg-[var(--surface)] text-muted-foreground">
          <LinkIcon className="size-5" />
        </div>
        <div className="space-y-3">
          <h1 className="text-3xl font-semibold tracking-[-0.03em] text-balance text-foreground">
            This verification link is incomplete.
          </h1>
          <p className="max-w-prose text-[0.9375rem] leading-relaxed text-pretty text-muted-foreground">
            The address is missing its{' '}
            <code className="font-mono text-foreground">token</code> parameter. Mail clients
            sometimes truncate long links — open the message again and follow the full URL,
            or start over and we will send a fresh one.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button asChild>
            <Link to="/register">Request a new link</Link>
          </Button>
          <Button variant="ghost" asChild>
            <Link to="/">Back to the overview</Link>
          </Button>
        </div>
      </div>
    </PublicLayout>
  )
}

function VerifiedSuccess({ result }: { result: VerifyResponse }) {
  const { login } = useAuth()

  // An invitation redeems into an account that already exists, so there is no
  // `main` zone of your own and the server omits `mcp_url` entirely. Saying
  // "your account starts with one zone" to an invitee would be a lie, and the
  // endpoint they need is whichever zone an ADMIN grants them next.
  const invited = result.mcp_url === undefined
  // A BILLING invitee has no zone and no MCP access, so there is no token to reveal (bug-221).
  const withToken = revealsToken(result)

  return (
    <PublicLayout>
      <div className="space-y-8">
        <div className="space-y-3">
          <p className="text-[0.6875rem] font-medium tracking-[0.12em] text-primary uppercase">
            {invited ? 'Invitation accepted' : 'Account created'}
          </p>
          <h1 className="text-3xl font-semibold tracking-[-0.03em] text-balance text-foreground">
            {invited ? `You have joined ${result.account.name}.` : 'Your endpoint is live.'}
          </h1>
          <p className="max-w-prose text-[0.9375rem] leading-relaxed text-pretty text-muted-foreground">
            {!withToken
              ? 'You have billing access to this account: its plan, invoices, payment methods and police balance. Billing members have no zones and no MCP access, so there is no bearer token to keep.'
              : invited
              ? 'Your token is yours alone and spans every zone you are granted. You cannot reach any zone yet — an administrator of this account decides which ones you join, and they appear in the console the moment they do.'
              : 'Your account starts with one zone, named main. Every client that points at this URL with your token joins that zone and can address every other connection in it by name. Create more zones from the console whenever you want a separate, isolated set.'}
          </p>
        </div>

        {withToken && result.token !== undefined ? (
          <SecretTokenPanel token={result.token} mcpUrl={result.mcp_url} />
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <p className="text-xs font-medium tracking-[0.06em] text-muted-foreground uppercase">
              Account slug
            </p>
            <CopyableValue value={result.account.slug} label="Copy account slug" />
          </div>
          <div className="space-y-1.5">
            <p className="text-xs font-medium tracking-[0.06em] text-muted-foreground uppercase">
              Signed up as
            </p>
            <CopyableValue value={result.user.email} label="Copy email" muted />
          </div>
        </div>

        {withToken ? (
        <Alert variant="info">
          <AlertTitle>Next: install it into your clients.</AlertTitle>
          <AlertDescription>
            Sign in to the console and open Setup. It renders a ready-made{' '}
            <code className="font-mono text-foreground">.mcp.json</code> with this endpoint
            already filled in, plus install scripts for macOS/Linux/Git Bash and Windows.
          </AlertDescription>
        </Alert>
        ) : null}

        <div className="flex flex-wrap items-center gap-3">
          <Button
            size="lg"
            onClick={() => {
              login(landingAfterVerify(result))
            }}
          >
            Sign in to the console
            <ArrowRightIcon className="size-4" />
          </Button>
          <Button variant="ghost" size="lg" asChild>
            <Link to="/">Not yet — take me home</Link>
          </Button>
        </div>
      </div>
    </PublicLayout>
  )
}

export function VerifyPage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const nameFieldId = useId()
  const passwordFieldId = useId()
  const confirmFieldId = useId()

  // undefined = untouched: the field shows the name the inviting ADMIN gave (bug-221).
  const [typedName, setName] = useState<string | undefined>(undefined)
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [localError, setLocalError] = useState<string | undefined>(undefined)

  const mutation = useMutation({
    mutationFn: (body: VerifyRequest) => verify(body),
  })

  const hasToken = token !== null && token.trim().length > 0
  // Reads what the link is for without redeeming it; a failure only means no pre-fill.
  const preview = useQuery({
    queryKey: ['invitation-preview', token],
    queryFn: () => invitationPreview(token ?? ''),
    enabled: hasToken,
    retry: false,
    staleTime: Infinity,
  })
  const name = nameFieldValue(typedName, preview.data)
  const billingInvitee = preview.data?.invited === true && preview.data.role === 'BILLING'

  if (token === null || !hasToken) {
    return <MissingToken />
  }

  if (mutation.isSuccess) {
    return <VerifiedSuccess result={mutation.data} />
  }

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    setLocalError(undefined)

    const trimmedName = name.trim()
    if (trimmedName.length === 0) {
      setLocalError('Enter the name you want on the account.')
      return
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setLocalError(`Use at least ${MIN_PASSWORD_LENGTH} characters.`)
      return
    }
    if (password !== confirmation) {
      setLocalError('The two passwords do not match.')
      return
    }

    mutation.mutate({ token, name: trimmedName, password })
  }

  const shownError = localError ?? (mutation.isError ? errorMessage(mutation.error) : undefined)

  return (
    <PublicLayout aside={<AsideNotes title="What happens here" items={ASIDE} />}>
      <div className="space-y-8">
        <div className="space-y-3">
          <p className="text-[0.6875rem] font-medium tracking-[0.12em] text-primary uppercase">
            Step 2 of 2
          </p>
          <h1 className="text-3xl font-semibold tracking-[-0.03em] text-balance text-foreground">
            Finish setting up your account.
          </h1>
          <p className="max-w-prose text-[0.9375rem] leading-relaxed text-pretty text-muted-foreground">
            {billingInvitee
              ? `Your email is verified. Confirm your name and choose a password — submitting this form creates your login with billing access to ${preview.data?.account_name ?? 'the account'}.`
              : 'Your email is verified. Choose a name and a password — submitting this form creates your login and issues your MCP endpoint and bearer token.'}
          </p>
        </div>

        <form onSubmit={onSubmit} className="max-w-md space-y-5" noValidate>
          <div className="space-y-2">
            <Label htmlFor={nameFieldId}>Your name</Label>
            <Input
              id={nameFieldId}
              name="name"
              autoComplete="name"
              required
              placeholder="Ada Lovelace"
              value={name}
              onChange={(event) => {
                setName(event.target.value)
              }}
              disabled={mutation.isPending}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor={passwordFieldId}>Password</Label>
            <Input
              id={passwordFieldId}
              type="password"
              name="new-password"
              autoComplete="new-password"
              required
              minLength={MIN_PASSWORD_LENGTH}
              value={password}
              onChange={(event) => {
                setPassword(event.target.value)
              }}
              disabled={mutation.isPending}
            />
            <p className="text-xs text-muted-foreground">
              At least {MIN_PASSWORD_LENGTH} characters. Your identity provider may require
              more.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor={confirmFieldId}>Confirm password</Label>
            <Input
              id={confirmFieldId}
              type="password"
              name="confirm-password"
              autoComplete="new-password"
              required
              value={confirmation}
              aria-invalid={confirmation.length > 0 && confirmation !== password}
              onChange={(event) => {
                setConfirmation(event.target.value)
              }}
              disabled={mutation.isPending}
            />
          </div>

          {shownError === undefined ? null : (
            <Alert variant="destructive">
              <AlertTitle>Could not complete verification</AlertTitle>
              <AlertDescription>{shownError}</AlertDescription>
            </Alert>
          )}

          <Button type="submit" size="lg" disabled={mutation.isPending}>
            {mutation.isPending ? 'Creating your account…' : 'Create account'}
            {mutation.isPending ? null : <ArrowRightIcon className="size-4" />}
          </Button>
        </form>
      </div>
    </PublicLayout>
  )
}
